/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import _ from 'lodash';
import { Container, Icon } from 'semantic-ui-react';
import { withRouter } from 'react-router-dom';
import { decorate, observable, runInAction, action, computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import TimeAgo from 'react-timeago';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreError, isStoreLoading, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import { createLink } from '@amzn/base-ui/dist/helpers/routing';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

import UpdateUser from './UpdateUser';
import Paginate from '../helpers/Paginate';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };
const PER_PAGE_DEFAULT = 50;

// Multi-sort: array of { field, desc } objects.
// Click = reset to single sort on that field (toggle direction if already sole sort).
// Ctrl/Cmd+click = add/toggle field in the sort stack.
function nextSortState(current, field, isCtrl) {
  const existing = current.findIndex(s => s.field === field);
  if (isCtrl) {
    const next = [...current];
    if (existing >= 0) {
      next[existing] = { field, desc: !next[existing].desc };
    } else {
      next.push({ field, desc: false });
    }
    return next;
  }
  // Plain click
  if (current.length === 1 && existing === 0) {
    return [{ field, desc: !current[0].desc }];
  }
  return [{ field, desc: false }];
}

function applySorts(list, sorts, accessors) {
  if (sorts.length === 0) return list;
  const fields = sorts.map(s => (item) => {
    const fn = accessors[s.field];
    return fn ? fn(item) : '';
  });
  const dirs = sorts.map(s => (s.desc ? 'desc' : 'asc'));
  return _.orderBy(list, fields, dirs);
}

class UsersList extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.search = '';
      this.sorts = [{ field: 'createdAt', desc: true }];
      this.page = 1;
      this.perPage = PER_PAGE_DEFAULT;
      this.statusFilter = 'all';
      this.openUserUid = null;
    });
  }

  componentDidMount() {
    const store = this.getStore();
    swallowError(store.load());
  }

  getStore() {
    return this.props.usersStore;
  }

  goto(pathname) {
    const { location, history } = this.props;
    const link = createLink({ location, pathname });
    history.push(link);
  }

  handleAddUser = () => { this.goto('/users/add'); };

  handleSearchChange = (e) => {
    this.search = e.target.value;
    this.page = 1;
  };

  handleSort = (field, e) => {
    this.sorts = nextSortState(this.sorts, field, e.ctrlKey || e.metaKey);
  };

  handlePageChange = (newPage) => { this.page = newPage; };
  handlePerPageChange = (newPerPage) => { this.perPage = newPerPage; this.page = 1; };

  handleStatusFilter = (status) => {
    this.statusFilter = status;
    this.page = 1;
  };

  // Accessor functions for sorting -- returns a comparable value for each field
  get sortAccessors() {
    return {
      firstName: u => `${u.firstName} ${u.lastName}`.toLowerCase(),
      email: u => (u.email || '').toLowerCase(),
      userRole: u => (u.userRole || '').toLowerCase(),
      status: u => u.status || '',
      createdAt: u => u.createdAt || '',
      lastLogin: u => u.lastLogin || '',
    };
  }

  get filteredUsers() {
    const store = this.getStore();
    let users = store.list;

    if (this.statusFilter !== 'all') {
      users = users.filter(u => u.status === this.statusFilter);
    }

    if (this.search.trim()) {
      const term = this.search.toLowerCase().trim();
      users = users.filter(u => {
        const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
        const email = (u.email || '').toLowerCase();
        const role = (u.userRole || '').toLowerCase();
        const projects = (u.projectId || []).join(' ').toLowerCase();
        return fullName.includes(term) || email.includes(term) || role.includes(term) || projects.includes(term);
      });
    }

    return applySorts(users, this.sorts, this.sortAccessors);
  }

  render() {
    const store = this.getStore();
    let content = null;
    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} />;
    } else if (isStoreLoading(store)) {
      content = <BasicProgressPlaceholder segmentCount={3} />;
    } else if (isStoreReady(store)) {
      content = this.renderMain();
    }

    return (
      <Container className="mt3 animated fadeIn">
        {this.renderHeader()}
        {content}
      </Container>
    );
  }

  renderHeader() {
    const store = this.getStore();
    const count = isStoreReady(store) ? store.list.length : null;

    return (
      <div className="workspace-list-header" style={{ marginBottom: '16px' }}>
        <div className="workspace-list-header-top" style={{ gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '1.15em', fontWeight: 600, color: 'var(--rmit-blue)' }}>
            <Icon name="users" style={{ ...iconStyle, marginRight: '8px' }} />
            Users
          </h3>
          {count !== null && (
            <span className="workspace-status-badge" style={{ background: '#f3f4f6', color: '#374151', fontSize: '0.75em' }}>
              {count} total
            </span>
          )}
        </div>
        <div className="workspace-list-header-actions">
          <button className="workspace-action-btn workspace-action-btn-primary" onClick={this.handleAddUser}>
            <Icon name="plus" style={iconStyle} /> Add Federated User
          </button>
        </div>
      </div>
    );
  }

  renderMain() {
    const filtered = this.filteredUsers;
    const firstIndex = (this.page - 1) * this.perPage;
    const pageUsers = filtered.slice(firstIndex, firstIndex + this.perPage);

    return (
      <div>
        {this.renderControls()}
        <div style={{ fontStyle: 'italic', color: '#9ca3af', textAlign: 'right', fontSize: '0.8em', marginBottom: '6px' }}>
          Hold Ctrl and click column headers to sort by multiple columns.
        </div>
        {this.renderTable(pageUsers)}
        <Paginate
          entriesPerPage={this.perPage}
          totalEntries={filtered.length}
          currentPage={this.page}
          onPageChange={this.handlePageChange}
          onPerPageChange={this.handlePerPageChange}
        />
      </div>
    );
  }

  renderControls() {
    const statusCounts = this.getStatusCounts();
    const filters = [
      { key: 'all', label: 'All', count: this.getStore().list.length },
      { key: 'active', label: 'Active', count: statusCounts.active },
      { key: 'inactive', label: 'Inactive', count: statusCounts.inactive },
    ];

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
            <Icon name="search" style={{
              position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
              color: '#9ca3af', margin: 0, lineHeight: 1, height: '1em', fontSize: '0.9em', pointerEvents: 'none',
            }} />
            <input
              type="text"
              placeholder="Search by name, email, role, or project..."
              value={this.search}
              onChange={this.handleSearchChange}
              style={{
                width: '100%', padding: '10px 14px 10px 38px', border: '1px solid #e5e7eb',
                borderRadius: '10px', fontSize: '0.9em', fontFamily: 'inherit', outline: 'none',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
              onFocus={e => { e.target.style.borderColor = 'var(--rmit-blue-50)'; e.target.style.boxShadow = '0 0 0 2px rgba(0, 0, 84, 0.08)'; }}
              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
        </div>
        <div className="workspace-filter-row-centered" style={{ marginTop: '0', marginBottom: '16px' }}>
          <div className="workspace-filter-container">
            {filters.map(f => (
              <button
                key={f.key}
                className={`workspace-filter-btn ${this.statusFilter === f.key ? 'selected' : ''}`}
                style={this.statusFilter === f.key
                  ? { background: 'var(--rmit-blue)', color: '#fff' }
                  : { background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }
                }
                onClick={() => this.handleStatusFilter(f.key)}
              >
                {f.label} <span style={{ opacity: 0.7, marginLeft: '4px' }}>{f.count}</span>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  }

  getStatusCounts() {
    const users = this.getStore().list;
    const counts = { active: 0, inactive: 0 };
    users.forEach(u => {
      if (counts[u.status] !== undefined) counts[u.status]++;
    });
    return counts;
  }

  renderSortHeader(label, field) {
    const sortEntry = this.sorts.find(s => s.field === field);
    const sortIndex = this.sorts.length > 1 ? this.sorts.findIndex(s => s.field === field) : -1;
    return (
      <th
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={(e) => this.handleSort(field, e)}
      >
        {label}
        {sortEntry && (
          <>
            <Icon
              name={sortEntry.desc ? 'caret down' : 'caret up'}
              style={{ ...iconStyle, marginLeft: '4px', fontSize: '0.8em' }}
            />
            {sortIndex >= 0 && (
              <span style={{ fontSize: '0.65em', color: '#9ca3af', marginLeft: '2px' }}>{sortIndex + 1}</span>
            )}
          </>
        )}
      </th>
    );
  }

  renderTable(pageUsers) {
    if (pageUsers.length === 0) {
      return (
        <div className="workspace-empty-state" style={{ marginTop: '24px' }}>
          <div className="workspace-empty-state-icon"><Icon name="users" /></div>
          <div className="workspace-empty-state-title">No users found</div>
          <div className="workspace-empty-state-subtitle">
            {this.search ? 'No users match your search criteria.' : 'No users in this category.'}
          </div>
        </div>
      );
    }

    return (
      <table className="config-table" style={{ marginTop: '8px' }}>
        <thead>
          <tr>
            {this.renderSortHeader('Status', 'status')}
            {this.renderSortHeader('Name', 'firstName')}
            {this.renderSortHeader('Email', 'email')}
            {this.renderSortHeader('Role', 'userRole')}
            <th>Projects</th>
            {this.renderSortHeader('Created', 'createdAt')}
            {this.renderSortHeader('Last Login', 'lastLogin')}
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {pageUsers.map(user => this.renderUserRow(user))}
        </tbody>
      </table>
    );
  }

  renderUserRow(user) {
    return (
      <tr key={user.id} onClick={() => this.handleRowClick(user)} style={{ cursor: 'pointer' }}>
        <td>{this.renderStatusBadge(user.status)}</td>
        <td><span className="config-name">{user.firstName} {user.lastName}</span></td>
        <td style={{ fontSize: '0.85em', color: '#374151' }}>{user.email}</td>
        <td>{this.renderRolePill(user.userRole)}</td>
        <td style={{ fontSize: '0.85em', color: '#6b7280' }}>
          {user.projectId && user.projectId.length > 0 ? user.projectId.join(', ') : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None</span>}
        </td>
        <td style={{ fontSize: '0.85em', color: '#6b7280' }}>
          {user.createdAt ? <TimeAgo date={user.createdAt} /> : '--'}
        </td>
        <td style={{ fontSize: '0.85em', color: '#6b7280' }}>
          {user.lastLogin
            ? <TimeAgo date={user.lastLogin} />
            : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Never</span>
          }
        </td>
        <td style={{ fontSize: '0.85em', color: '#6b7280' }}>
          {user.notes || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>None</span>}
        </td>
        <td style={{ padding: 0, width: 0, border: 'none', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
          <UpdateUser
            user={user}
            adminMode
            rowClickMode
            isOpen={this.openUserUid === user.uid}
            onClose={() => this.handleDetailClose()}
            userStore={this.props.userStore}
            usersStore={this.props.usersStore}
            userRolesStore={this.props.userRolesStore}
            awsAccountsStore={this.props.awsAccountsStore}
            projectsStore={this.props.projectsStore}
          />
        </td>
      </tr>
    );
  }

  handleRowClick = (user) => {
    this.openUserUid = user.uid;
  };

  handleDetailClose = () => {
    this.openUserUid = null;
  };

  renderRolePill(role) {
    if (!role) return <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '0.85em' }}>N/A</span>;
    const lower = role.toLowerCase();
    if (lower === 'admin') {
      return <span className="study-pill" style={{ background: '#f0f0ff', color: 'var(--rmit-blue)' }}>Admin</span>;
    }
    if (lower === 'researcher') {
      return <span className="study-pill" style={{ background: '#f0fdf4', color: '#15803d' }}>Researcher</span>;
    }
    // Fallback for any other role
    return <span className="study-pill" style={{ background: '#f3f4f6', color: '#374151' }}>{role}</span>;
  }

  renderStatusBadge(status) {
    const styles = {
      active: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
      inactive: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
    };
    const labels = { active: 'Active', inactive: 'Inactive' };
    const s = styles[status] || { background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' };
    return (
      <span className="workspace-status-badge" style={s}>
        {labels[status] || status}
      </span>
    );
  }
}

decorate(UsersList, {
  search: observable,
  sorts: observable,
  page: observable,
  perPage: observable,
  statusFilter: observable,
  openUserUid: observable,
  filteredUsers: computed,
  sortAccessors: computed,
  handleAddUser: action,
  handleSearchChange: action,
  handleSort: action,
  handlePageChange: action,
  handlePerPageChange: action,
  handleStatusFilter: action,
  handleRowClick: action,
  handleDetailClose: action,
});

export default inject(
  'userStore',
  'usersStore',
  'userRolesStore',
  'awsAccountsStore',
  'projectsStore',
)(withRouter(observer(UsersList)));
